import { useEffect, useRef } from 'react';
import { FAITH_CATEGORY_EMOJIS, QUICK_REACT_EMOJIS } from '@/config/emoji-config';

/** Curated general emojis for the picker (faith tab comes first). */
const GENERAL_EMOJIS = [
  '😀', '😃', '😄', '😁', '😅', '🤣', '😂', '🙂', '😉', '😊',
  '😍', '🥰', '😘', '😎', '🤗', '🤔', '😮', '😢', '😭', '😤',
  '🥳', '😴', '🤤', '🤝', '👋', '✋', '👌', '✌️', '🤞', '👏',
  '🙌', '👍', '👎', '💪', '❤️', '🧡', '💛', '💚', '💙', '💜',
  '🖤', '🤍', '💯', '✨', '🌟', '⭐', '🔥', '🎉', '🎊', '🌈',
  '☀️', '🌻', '🌸', '🍞', '🐑', '🕊️', '🎵', '📖', '💡', '🏠',
];

interface Props {
  onSelect: (emoji: string) => void;
  onClose: () => void;
  /** Anchor classes relative to the toolbar (horizontal + vertical). */
  align?: string;
  /** Render in normal flow (inside a modal) instead of as a popover. */
  inline?: boolean;
}

/** Full reaction picker: Faith tab first, then general emojis. */
export default function ReactionPicker({
  onSelect,
  onClose,
  align = 'right-0 top-8',
  inline = false,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (inline) return; // the host modal owns dismissal
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, inline]);

  return (
    <div
      ref={ref}
      className={
        inline
          ? 'w-full bg-white p-3'
          : `absolute ${align} z-30 w-72 rounded-xl border border-flock-gray-200 bg-white p-3 shadow-2xl`
      }
    >
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-flock-navy-400">
        Quick
      </div>
      <div className="mb-2 flex flex-wrap gap-1">
        {QUICK_REACT_EMOJIS.map((e) => (
          <button
            key={e}
            onClick={() => onSelect(e)}
            className="rounded-lg p-1.5 text-xl hover:bg-flock-navy-50"
          >
            {e}
          </button>
        ))}
      </div>
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-flock-navy-400">
        Faith
      </div>
      <div className="mb-2 flex flex-wrap gap-1">
        {FAITH_CATEGORY_EMOJIS.map((e) => (
          <button
            key={e}
            onClick={() => onSelect(e)}
            className="rounded-lg p-1.5 text-xl hover:bg-flock-navy-50"
          >
            {e}
          </button>
        ))}
      </div>
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-flock-navy-400">
        More
      </div>
      <div className="flex max-h-36 flex-wrap gap-1 overflow-y-auto">
        {GENERAL_EMOJIS.map((e) => (
          <button
            key={e}
            onClick={() => onSelect(e)}
            className="rounded-lg p-1.5 text-xl hover:bg-flock-navy-50"
          >
            {e}
          </button>
        ))}
      </div>
    </div>
  );
}
